import { type CTNDropdownOptionProps } from '@ctn/ui/types/ctn-dropdown';
import { create } from 'zustand';

import { getOptionsMaestro } from '@/helpers/dropdown';
import { type IMaestro, type IMaestrosGeneric, type ImaestrosTramite } from '@/interfaces/model/maestros.interface';

// Transforma cada clave de los maestros en un array de opciones para el dropdown
type OptionsGenericMap = Record<keyof IMaestrosGeneric, CTNDropdownOptionProps[]>;
type OptionsTramiteMap = Record<keyof ImaestrosTramite, CTNDropdownOptionProps[]>;

/** Unifica los maestros genéricos con los campos calculados específicos. */
type OptionsData = {
	prefijoPaises: CTNDropdownOptionProps[];
	tramite: OptionsTramiteMap;
} & OptionsGenericMap;

type OptionsState = {
	options: Partial<OptionsData>;
	setOptionsFromContext: (
		maestrosGeneric: IMaestrosGeneric | undefined,
		maestrosTramite: ImaestrosTramite | undefined,
		tramiteId: number,
	) => void;
};

const useOptionsStore = create<OptionsState>((set) => ({
	options: {},

	setOptionsFromContext(maestrosGeneric, maestrosTramite) {
		if (!maestrosGeneric || !maestrosTramite) return;

		const { provincias, tipoVia, paises, ...restGeneric } = maestrosGeneric;

		const optionPaises: CTNDropdownOptionProps[] = paises.map((p) => ({
			value: p.codigo,
			label: p.descripcion,
		}));

		const optionPrefijoPaises: CTNDropdownOptionProps[] = paises.map((p) => ({
			value: p.codigo,
			label: `+ ${p.prefijoTelefonico?.[0]?.codigo ?? ''}`,
		}));

		const optionProvincias: CTNDropdownOptionProps[] = provincias.map((p) => ({
			value: p.codigo,
			label: p.nombre,
		}));

		const optionTipoVia: CTNDropdownOptionProps[] = tipoVia.map((p) => ({
			value: p.codigo,
			label: p.descripcion,
		}));

		const optionsGenericRest = Object.entries(restGeneric).reduce<Partial<OptionsGenericMap>>(
			(acc, [key, valor]) => {
				if (Array.isArray(valor)) {
					acc[key as keyof IMaestrosGeneric] = getOptionsMaestro(valor as IMaestro[]);
				}
				return acc;
			},
			{},
		);

		const optionsTramite = Object.entries(maestrosTramite).reduce<OptionsTramiteMap>((acc, [key, valor]) => {
			if (Array.isArray(valor)) {
				acc[key as keyof ImaestrosTramite] = getOptionsMaestro(valor as IMaestro[]);
			}
			return acc;
			// eslint-disable-next-line @typescript-eslint/prefer-reduce-type-parameter
		}, {} as OptionsTramiteMap);

		const allOptions: OptionsData = {
			...(optionsGenericRest as OptionsGenericMap), // El resto de genéricos
			paises: optionPaises,
			provincias: optionProvincias,
			tipoVia: optionTipoVia,
			prefijoPaises: optionPrefijoPaises,
			tramite: optionsTramite,
		};

		set({ options: allOptions });
	},
}));

export default useOptionsStore;
